/**
 * Fleet sync health — noticing that a device has stopped delivering evidence.
 *
 * ADR-0001 §4.2 and §4.6 guarantee a device never *loses* data. They do not guarantee
 * anyone finds out when a device stops *delivering* it. A phone in a truck console with
 * forty unsynced photos is, from the office's point of view, indistinguishable from a
 * quiet day — right up until an adjuster asks for the test squares and the roof has
 * already been replaced. That failure is silent, slow, and only surfaces at the worst
 * possible moment. This module is how it stops being silent.
 *
 * **The central design constraint: the failing device cannot be the thing that reports
 * the failure.** A phone that is off, broken, out of coverage, or has had the app
 * force-quit for three days sends nothing at all. Any alerting scheme built on "the
 * device tells us it is unhealthy" is blind to exactly the case that matters most.
 * So health here is derived from what the *server* observed, and above all from what it
 * has stopped observing:
 *
 *  - **Backlog age** — how long the server has continuously known this device is
 *    carrying unsynced evidence, measured on the server clock (`backlogSince`).
 *  - **Silence** — how long since the device last made contact at all, measured on the
 *    server clock, and treated as an alarm *only* when the last thing we heard was that
 *    it was holding evidence. A device with an empty outbox going quiet is a device
 *    parked for the weekend, not an incident.
 *
 * Both are server-anchored on purpose. ADR-0001 §4.5 forbids trusting device clocks for
 * ordering, and the same reasoning applies to duration: a phone with a wrong clock would
 * otherwise be able to report its three-day-old backlog as fresh and suppress its own
 * alarm. The device's own numbers are still carried (they are the only source for
 * *what* is queued) but never for *how long*.
 *
 * Nothing here imports React, Dexie, or `fetch` (ADR-0001 §3.1). The evaluation is a
 * pure function over stored state so it can run in the API, in a scheduled job, or in a
 * test with no clock.
 */

import type { Uuid } from './ids.js';
import type { OutboxRecord } from './ports.js';

// -- what a device tells us ---------------------------------------------------

/**
 * A device's account of its own outbox, piggybacked on every sync push.
 *
 * Deliberately cheap — a handful of integers — because it rides on a request a phone on
 * bad LTE is already struggling to complete. It is an optimisation, not the source of
 * truth: everything time-based is recomputed from the server's own receipt clock.
 */
export interface DeviceSyncReport {
  readonly deviceId: Uuid;
  readonly orgId: Uuid;
  /**
   * The device's belief about now. Recorded to detect clock skew, which corrupts every
   * "captured at" we display (ADR-0001 §4.5). Never used to compute an age.
   */
  readonly reportedAt: string;
  /** Ops written locally and not yet confirmed by the server. */
  readonly unsyncedOps: number;
  /** Ops the server rejected terminally. These never self-heal (see `ports.ts`). */
  readonly failedOps: number;
  /** Photos whose bytes exist nowhere but this device (ADR-0001 §4.6 step 6). */
  readonly photosHeldLocally: number;
  readonly bytesHeldLocally: number;
  /** Free space the browser will still grant us, when the platform reports it. */
  readonly storageQuotaBytes?: number;
}

/**
 * Builds a report from the device's own outbox. Pure, so the numbers on the office
 * screen and the numbers in the crew's sync banner come from one implementation.
 */
export function summarizeOutbox(
  records: Iterable<OutboxRecord>,
  device: {
    readonly deviceId: Uuid;
    readonly orgId: Uuid;
    readonly reportedAt: string;
    readonly photosHeldLocally: number;
    readonly bytesHeldLocally: number;
    readonly storageQuotaBytes?: number;
  },
): DeviceSyncReport {
  let unsyncedOps = 0;
  let failedOps = 0;
  for (const record of records) {
    // `synced` is the only status that releases the client's obligation (`ports.ts`).
    // Everything else is still owed, and `failed` is owed *and* stuck.
    if (record.status === 'synced') continue;
    unsyncedOps += 1;
    if (record.status === 'failed') failedOps += 1;
  }
  return {
    deviceId: device.deviceId,
    orgId: device.orgId,
    reportedAt: device.reportedAt,
    unsyncedOps,
    failedOps,
    photosHeldLocally: device.photosHeldLocally,
    bytesHeldLocally: device.bytesHeldLocally,
    ...(device.storageQuotaBytes === undefined
      ? {}
      : { storageQuotaBytes: device.storageQuotaBytes }),
  };
}

// -- what the server remembers ------------------------------------------------

/**
 * Per-device state the server persists between contacts.
 *
 * This row is what makes silence detectable. Evaluation reads stored state rather than
 * live traffic, so a device that sends nothing still gets evaluated — and a device that
 * has never been heard from since it was enrolled is not invisible.
 */
export interface DeviceSyncState {
  readonly deviceId: Uuid;
  readonly orgId: Uuid;
  /** Human label for the office screen: "Truck 4 — Marco". Falls back to the id. */
  readonly label?: string;
  /** Server receipt time of the most recent contact. Epoch ms, server clock. */
  readonly lastContactAt: number;
  /**
   * Server receipt time of the earliest contact in the current unbroken run of
   * "this device is holding unsynced evidence". Cleared the moment it reports empty.
   *
   * This is the honest measure of how long evidence has been stranded, and it is why
   * `observeReport` is a reducer rather than a plain overwrite: the duration lives
   * across contacts, and no single report contains it.
   */
  readonly backlogSince?: number;
  /** The most recent report. Counts only — never consulted for durations. */
  readonly lastReport: DeviceSyncReport;
  /** `reportedAt` minus server receipt time, most recent contact. Signed, epoch ms. */
  readonly lastClockSkewMs: number;
  /** Set when an operator has consciously accepted this device's state. See §alarms. */
  readonly mutedUntil?: number;
}

/**
 * Folds a new report into stored state.
 *
 * `receivedAt` is the server's clock at the moment the request landed — the same
 * "honest timestamp" the event log records as `received_at` (ADR-0001 §4.7).
 */
export function observeReport(
  previous: DeviceSyncState | undefined,
  report: DeviceSyncReport,
  receivedAt: number,
): DeviceSyncState {
  const holdingEvidence = report.unsyncedOps > 0;
  const parsed = Date.parse(report.reportedAt);
  const skew = Number.isNaN(parsed) ? 0 : parsed - receivedAt;

  // A backlog that has never cleared keeps its original start time. A device that
  // reports empty clears it, so the next backlog is timed from its own beginning
  // rather than inheriting the age of one that already drained.
  const backlogSince = holdingEvidence
    ? (previous?.backlogSince ?? receivedAt)
    : undefined;

  return {
    deviceId: report.deviceId,
    orgId: report.orgId,
    ...(previous?.label === undefined ? {} : { label: previous.label }),
    lastContactAt: receivedAt,
    ...(backlogSince === undefined ? {} : { backlogSince }),
    lastReport: report,
    lastClockSkewMs: skew,
    ...(previous?.mutedUntil === undefined ? {} : { mutedUntil: previous.mutedUntil }),
  };
}

// -- policy -------------------------------------------------------------------

export interface SyncHealthPolicy {
  /** Backlog carried this long is worth a look. */
  readonly backlogWarnMs: number;
  /** Backlog carried this long is an incident. */
  readonly backlogAlarmMs: number;
  /** Silence this long, from a device last seen holding evidence, is worth a look. */
  readonly silenceWarnMs: number;
  /** Silence this long, from a device last seen holding evidence, is an incident. */
  readonly silenceAlarmMs: number;
  /** Device clock this far from ours corrupts every "captured at" it produces. */
  readonly clockSkewWarnMs: number;
  /** Local bytes above this mean the device is approaching a capture failure. */
  readonly localBytesWarnBytes: number;
}

const HOUR = 60 * 60 * 1000;

/**
 * Defaults, and the reasoning, because a threshold with no argument behind it gets
 * tuned to whatever silences the pager.
 *
 * The unit that matters is **the working day**, not an SLA percentile. A crew leaves in
 * the morning, works roofs, and comes back into coverage — office wifi, a coffee stop,
 * anywhere with bars — by evening. So:
 *
 *  - **4h warn.** Long enough that a rep genuinely out of coverage all morning does not
 *    trip it, short enough that someone can still call them *that day*, while the roof
 *    is still standing and a missed shot can still be retaken. That last clause is the
 *    entire point: after the roof is torn off, no alert is actionable.
 *  - **12h alarm.** A backlog that survives 12 hours has crossed an evening. The day's
 *    evidence did not land, and normal behaviour did not fix it. That is an incident
 *    even if the device is fine, because we can no longer assume it will self-resolve.
 *
 * Terminal failures are not on this scale at all — see `evaluateDevice`.
 */
export const DEFAULT_HEALTH_POLICY: SyncHealthPolicy = {
  backlogWarnMs: 4 * HOUR,
  backlogAlarmMs: 12 * HOUR,
  silenceWarnMs: 4 * HOUR,
  silenceAlarmMs: 12 * HOUR,
  clockSkewWarnMs: 5 * 60 * 1000,
  localBytesWarnBytes: 2 * 1024 * 1024 * 1024,
};

// -- evaluation ---------------------------------------------------------------

export type HealthSeverity = 'ok' | 'warn' | 'alarm';

export type HealthReasonCode =
  | 'backlog_age'
  | 'device_silent'
  | 'terminal_failures'
  | 'local_storage_pressure'
  | 'clock_skew';

export interface HealthReason {
  readonly code: HealthReasonCode;
  readonly severity: 'warn' | 'alarm';
  /** Written to be read by whoever is woken up, with the numbers already in it. */
  readonly message: string;
  readonly observedMs?: number;
  readonly thresholdMs?: number;
}

export interface DeviceHealth {
  readonly deviceId: Uuid;
  readonly orgId: Uuid;
  readonly label: string;
  readonly severity: HealthSeverity;
  /** Most severe first, so a UI can render `reasons[0]` and be right. */
  readonly reasons: readonly HealthReason[];
  readonly unsyncedOps: number;
  readonly failedOps: number;
  readonly photosHeldLocally: number;
  readonly bytesHeldLocally: number;
  /** Server-measured. Undefined when the device is not carrying a backlog. */
  readonly backlogAgeMs?: number;
  readonly silenceMs: number;
  /**
   * This device holds photo bytes that exist nowhere else on earth (ADR-0001 §4.6).
   * Not a severity by itself — it is the reason severity here is worth paging over.
   */
  readonly holdsSoleCopy: boolean;
  readonly muted: boolean;
}

const RANK: Record<HealthSeverity, number> = { ok: 0, warn: 1, alarm: 2 };

function worst(severities: Iterable<HealthSeverity>): HealthSeverity {
  let out: HealthSeverity = 'ok';
  for (const s of severities) if (RANK[s] > RANK[out]) out = s;
  return out;
}

function describeDuration(ms: number): string {
  if (ms < HOUR) return `${Math.max(1, Math.round(ms / 60_000))}m`;
  if (ms < 48 * HOUR) return `${Math.round(ms / HOUR)}h`;
  return `${Math.round(ms / (24 * HOUR))}d`;
}

/**
 * Scores one device against the policy.
 *
 * `now` is the server clock. Pure: same inputs, same verdict, no ambient time.
 */
export function evaluateDevice(
  state: DeviceSyncState,
  policy: SyncHealthPolicy = DEFAULT_HEALTH_POLICY,
  now: number = Date.now(),
): DeviceHealth {
  const label = state.label ?? state.deviceId;
  const report = state.lastReport;
  const silenceMs = Math.max(0, now - state.lastContactAt);
  const backlogAgeMs =
    state.backlogSince === undefined ? undefined : Math.max(0, now - state.backlogSince);
  const holdsSoleCopy = report.photosHeldLocally > 0;
  const muted = state.mutedUntil !== undefined && state.mutedUntil > now;

  const reasons: HealthReason[] = [];

  if (backlogAgeMs !== undefined && backlogAgeMs >= policy.backlogWarnMs) {
    const alarming = backlogAgeMs >= policy.backlogAlarmMs;
    reasons.push({
      code: 'backlog_age',
      severity: alarming ? 'alarm' : 'warn',
      message:
        `${label} has been holding ${report.unsyncedOps} unsynced ` +
        `${report.unsyncedOps === 1 ? 'record' : 'records'}` +
        (holdsSoleCopy ? ` (${report.photosHeldLocally} photos with no other copy)` : '') +
        ` for ${describeDuration(backlogAgeMs)}.`,
      observedMs: backlogAgeMs,
      thresholdMs: alarming ? policy.backlogAlarmMs : policy.backlogWarnMs,
    });
  }

  // Silence only counts against a device that went quiet *while owing us evidence*.
  // Otherwise every phone on a charger overnight pages someone, the alerts get muted as
  // a class, and the one that mattered is muted along with them.
  if (report.unsyncedOps > 0 && silenceMs >= policy.silenceWarnMs) {
    const alarming = silenceMs >= policy.silenceAlarmMs;
    reasons.push({
      code: 'device_silent',
      severity: alarming ? 'alarm' : 'warn',
      message:
        `${label} has not checked in for ${describeDuration(silenceMs)}. ` +
        `Last contact it was holding ${report.unsyncedOps} unsynced ` +
        `${report.unsyncedOps === 1 ? 'record' : 'records'}` +
        (holdsSoleCopy ? `, including ${report.photosHeldLocally} photos with no other copy` : '') +
        `.`,
      observedMs: silenceMs,
      thresholdMs: alarming ? policy.silenceAlarmMs : policy.silenceWarnMs,
    });
  }

  // Terminal failures get no grace period and no duration threshold. A retryable error
  // is the network being the network; a terminal one means the server has refused this
  // op and will refuse it forever, so the entity is quarantined (`ports.ts`) and the
  // queue behind it will never drain on its own. Waiting four hours to mention that
  // only delays the human action it already requires.
  if (report.failedOps > 0) {
    reasons.push({
      code: 'terminal_failures',
      severity: 'alarm',
      message:
        `${label} has ${report.failedOps} permanently rejected ` +
        `${report.failedOps === 1 ? 'op' : 'ops'}. These will never send without ` +
        `operator action, and the affected entities are quarantined.`,
    });
  }

  if (report.bytesHeldLocally >= policy.localBytesWarnBytes) {
    reasons.push({
      code: 'local_storage_pressure',
      severity: 'warn',
      message:
        `${label} is holding ${(report.bytesHeldLocally / 1024 ** 3).toFixed(1)} GB ` +
        `locally. A device that runs out of space cannot capture.`,
    });
  }

  if (Math.abs(state.lastClockSkewMs) >= policy.clockSkewWarnMs) {
    reasons.push({
      code: 'clock_skew',
      severity: 'warn',
      message:
        `${label}'s clock is off by ${describeDuration(Math.abs(state.lastClockSkewMs))}. ` +
        `Its "captured at" timestamps will be wrong by that much.`,
    });
  }

  reasons.sort((a, b) => RANK[b.severity] - RANK[a.severity]);
  const severity = muted ? 'ok' : worst(reasons.map((r) => r.severity));

  return {
    deviceId: state.deviceId,
    orgId: state.orgId,
    label,
    severity,
    reasons,
    unsyncedOps: report.unsyncedOps,
    failedOps: report.failedOps,
    photosHeldLocally: report.photosHeldLocally,
    bytesHeldLocally: report.bytesHeldLocally,
    ...(backlogAgeMs === undefined ? {} : { backlogAgeMs }),
    silenceMs,
    holdsSoleCopy,
    muted,
  };
}

export interface FleetTotals {
  readonly devices: number;
  readonly unsyncedOps: number;
  readonly photosHeldLocally: number;
  readonly bytesHeldLocally: number;
  /** Devices that are the only place some photo exists. The number that matters. */
  readonly devicesHoldingSoleCopy: number;
}

export interface FleetHealth {
  readonly evaluatedAt: number;
  readonly severity: HealthSeverity;
  /** Worst first, then longest-suffering. The office view renders this in order. */
  readonly devices: readonly DeviceHealth[];
  readonly totals: FleetTotals;
}

/** Scores every enrolled device, including the ones that have gone quiet. */
export function evaluateFleet(
  states: Iterable<DeviceSyncState>,
  policy: SyncHealthPolicy = DEFAULT_HEALTH_POLICY,
  now: number = Date.now(),
): FleetHealth {
  const devices = [...states].map((s) => evaluateDevice(s, policy, now));

  devices.sort((a, b) => {
    if (RANK[b.severity] !== RANK[a.severity]) return RANK[b.severity] - RANK[a.severity];
    return (b.backlogAgeMs ?? b.silenceMs) - (a.backlogAgeMs ?? a.silenceMs);
  });

  const totals = devices.reduce<FleetTotals>(
    (acc, d) => ({
      devices: acc.devices + 1,
      unsyncedOps: acc.unsyncedOps + d.unsyncedOps,
      photosHeldLocally: acc.photosHeldLocally + d.photosHeldLocally,
      bytesHeldLocally: acc.bytesHeldLocally + d.bytesHeldLocally,
      devicesHoldingSoleCopy: acc.devicesHoldingSoleCopy + (d.holdsSoleCopy ? 1 : 0),
    }),
    { devices: 0, unsyncedOps: 0, photosHeldLocally: 0, bytesHeldLocally: 0, devicesHoldingSoleCopy: 0 },
  );

  return {
    evaluatedAt: now,
    severity: worst(devices.map((d) => d.severity)),
    devices,
    totals,
  };
}
